import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { displayName } from '../../utils/userLabel';
import ReferenceSelect from '../ReferenceSelect';
import * as S from '../specimens/styles';

/*
 * The sequencing conclusion of one extract (migration 0085; PUT
 * /dna/{id}/conclusion): after the sequences were analysed and queried
 * against BLAST, the final genetic identification a person reached -- the
 * taxon, to what level and how sure, the run it rests on, and the reasoning
 * (the hits, identity and coverage). Kept with who concluded it and when;
 * it marks the isolate's DNA stage identified. Whoever records DNA work
 * writes or withdraws it; everyone who reads the extract sees it.
 *
 * `extract` is the dossier's (its `conclusion` and `runs`); `onSaved` re-reads
 * the row and its dossier.
 */
const EMPTY = { taxon_name: '', level_id: '', confidence_id: '', run_id: '', text: '' };

export default function GeneticConclusion({ extract, canEdit = false, onSaved }) {
  const { t } = useTranslation('dna');
  const c = extract.conclusion;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const runs = extract.runs || [];

  const open = () => {
    setForm(c ? { taxon_name: c.taxon_name || '', level_id: c.level_id || '', confidence_id: c.confidence_id || '',
      run_id: c.run_id || '', text: c.text || '' } : EMPTY);
    setError('');
    setEditing(true);
  };
  const send = async (body) => {
    setBusy(true);
    setError('');
    try {
      await api.put(`/dna/${extract.dna_id}/conclusion`, body);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.code === 'conclusion_required' ? t('genetic.conclusion.required') : errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const submit = (e) => {
    e.preventDefault();
    send({ taxon_name: form.taxon_name.trim() || null, level_id: form.level_id || null,
      confidence_id: form.confidence_id || null, run_id: form.run_id || null, text: form.text.trim() || null });
  };
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value?.target ? value.target.value : value }));

  return (
    <section style={box} aria-label={t('genetic.conclusion.title')} data-testid={`conclusion-${extract.dna_id}`}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{t('genetic.conclusion.title')}</strong>
        {!editing && canEdit && (
          <button type="button" style={small} onClick={open} data-testid="conclusion-edit"
            title={t('genetic.conclusion.tip.edit')}>{c ? t('genetic.conclusion.edit') : t('genetic.conclusion.add')}</button>
        )}
      </div>

      {!editing && !c && <p style={{ ...S.hint, margin: '4px 0 0' }}>{t('genetic.conclusion.none')}</p>}
      {!editing && c && (
        <div style={{ fontSize: 14, marginTop: 4 }} data-testid="conclusion-view">
          {c.taxon_name && (
            <div><bdi dir="ltr" style={{ fontStyle: 'italic', fontWeight: 600 }} data-testid="conclusion-taxon">
              {c.taxon_name}</bdi>
              {[c.level_name, c.confidence_name].filter(Boolean).length > 0 && (
                <span style={S.hint}> · {[c.level_name, c.confidence_name].filter(Boolean).join(' · ')}</span>
              )}
            </div>
          )}
          {c.run_code && <div style={S.hint}>{t('genetic.conclusion.basedOn')} <bdi dir="ltr" style={S.mono}>{c.run_code}</bdi></div>}
          {c.text && <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }} dir="auto">{c.text}</p>}
          <div style={S.hint}>
            {t('genetic.conclusion.by', { who: displayName(c.concluded_by_name, t) || '—',
              when: String(c.concluded_at || '').slice(0, 10) })}
          </div>
        </div>
      )}

      {editing && (
        <form onSubmit={submit} style={{ display: 'grid', gap: 8, marginTop: 6, maxWidth: 620 }}
          data-testid="conclusion-form">
          <label style={lbl}><span>{t('genetic.conclusion.taxon')}</span>
            <input value={form.taxon_name} onChange={set('taxon_name')} maxLength={300} dir="ltr"
              style={{ ...S.input, fontStyle: 'italic' }} data-testid="conclusion-taxon-input"
              placeholder="Pleurotus ostreatus" /></label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label style={lbl}><span>{t('genetic.conclusion.level')}</span>
              <ReferenceSelect category="identification-levels" value={form.level_id} onChange={set('level_id')} /></label>
            <label style={lbl}><span>{t('genetic.conclusion.confidence')}</span>
              <ReferenceSelect category="confidence-levels" value={form.confidence_id}
                onChange={set('confidence_id')} /></label>
            <label style={lbl}><span>{t('genetic.conclusion.run')}</span>
              <select value={form.run_id} onChange={set('run_id')} style={S.input} data-testid="conclusion-run">
                <option value="">—</option>
                {runs.map((r) => (
                  <option key={r.run_id} value={r.run_id}>
                    {[r.run_code, r.gene_name, r.amplification_primers_name].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </select></label>
          </div>
          <label style={lbl}><span>{t('genetic.conclusion.text')}</span>
            <textarea value={form.text} onChange={set('text')} rows={4} maxLength={4000} dir="auto" style={S.input}
              placeholder={t('genetic.conclusion.textHint')} data-testid="conclusion-text" /></label>
          {error && <p role="alert" style={S.error}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="submit" style={S.btn()} disabled={busy || (!form.taxon_name.trim() && !form.text.trim())}
              data-testid="conclusion-save">{t('genetic.conclusion.save')}</button>
            <button type="button" style={S.ghostBtn} onClick={() => setEditing(false)} disabled={busy}>
              {t('genetic.cancel')}</button>
            {c && (
              <button type="button" style={{ ...S.ghostBtn, color: 'var(--c-danger-fg)' }} disabled={busy}
                onClick={() => send({ clear: true })} data-testid="conclusion-clear">
                {t('genetic.conclusion.withdraw')}</button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

const box = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 10, marginTop: 8 };
const lbl = { display: 'grid', gap: 4 };
const small = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
